import React, { useMemo, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { addMonths, differenceInCalendarMonths, format, parseISO } from "date-fns";
import { Calculator, Link2Off, Zap } from "lucide-react";
import MortgageSimulatorDialog from "@/components/MortgageSimulatorDialog";
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceLine,
} from "recharts";

// Hoisted stable style objects (avoid new refs on every render)
const AXIS_TICK = { fontSize: 11, fill: "#8A9992" };
const TOOLTIP_CONTENT_STYLE = { background: "#fff", border: "1px solid #EBEBEA", borderRadius: 12 };
const LEGEND_STYLE = { fontSize: 12 };

/**
 * Build monthly amortization schedule using the French system (cuota constante).
 * If monthly_payment is provided, use it; otherwise compute from principal/rate/term.
 */
function buildSchedule(debt) {
  const P = Number(debt.original_amount || 0);
  const remaining = Number(debt.remaining_amount || 0);
  const annualRate = Number(debt.interest_rate || 0) / 100;
  const i = annualRate / 12;
  const n = Number(debt.term_months || 0);
  if (!P || !n) return { rows: [], monthly: null };

  let monthly = Number(debt.monthly_payment || 0);
  if (!monthly) {
    monthly = i > 0
      ? (P * i) / (1 - Math.pow(1 + i, -n))
      : P / n;
  }

  const start = debt.start_date ? new Date(debt.start_date) : new Date();
  // Determine current month index based on remaining balance (approx.)
  let balance = P;
  let currentIdx = 0;
  for (let k = 0; k < n; k++) {
    const interest = balance * i;
    const principal = monthly - interest;
    balance = Math.max(0, balance - principal);
    if (balance <= remaining + 0.5) {
      currentIdx = k + 1;
      break;
    }
  }

  // Now rebuild full schedule
  balance = P;
  const rows = [];
  for (let k = 0; k < n; k++) {
    const interest = balance * i;
    const principal = monthly - interest;
    const newBalance = Math.max(0, balance - principal);
    rows.push({
      n: k + 1,
      date: format(addMonths(start, k), "MMM yyyy"),
      payment: monthly,
      interest,
      principal,
      balance: newBalance,
      isPast: k + 1 < currentIdx,
      isCurrent: k + 1 === currentIdx,
    });
    balance = newBalance;
  }

  return { rows, monthly, currentIdx };
}

export default function AmortizationDialog({ open, onOpenChange, debt }) {
  const { rows, monthly, currentIdx } = useMemo(
    () => (debt ? buildSchedule(debt) : { rows: [], monthly: null, currentIdx: 0 }),
    [debt]
  );

  // YTD summary from the backend (uses real linked transactions + extras)
  const [ytd, setYtd] = useState(null);
  // Linked transactions (for the extra-amortization markers timeline)
  const [linked, setLinked] = useState(null);
  const [simOpen, setSimOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!open || !debt?.id) { setYtd(null); setLinked(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const [ytdRes, linkedRes] = await Promise.all([
          api.get(`/debts/${debt.id}/amortization-summary`),
          api.get(`/debts/${debt.id}/linked-transactions`),
        ]);
        if (!cancelled) {
          setYtd(ytdRes.data);
          setLinked(linkedRes.data);
        }
      } catch (e) {
        logger.warn("amortization dialog load", e);
        if (!cancelled) { setYtd(null); setLinked(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, debt?.id, refreshTick]);

  // Extras with month-index mapped to the current schedule so we can annotate
  // the stacked area chart. Uses date-fns to compute months since start_date.
  const extras = useMemo(() => {
    const payments = linked?.payments || [];
    const start = debt?.start_date ? parseISO(debt.start_date) : null;
    if (!start) return [];
    return payments
      .filter((p) => p.is_extra_amortization)
      .map((p) => {
        let n = null;
        try {
          const d = parseISO(p.date);
          n = differenceInCalendarMonths(d, start) + 1; // 1-based to match chart
        } catch (e) {
          logger.warn("amort dialog: parse extra date failed", e);
        }
        return { ...p, n };
      })
      .filter((p) => p.n !== null && p.n > 0);
  }, [linked, debt?.start_date]);

  const unlinkExtra = async (tx) => {
    try {
      // Simply clear the extra flag; the debt is auto-recomputed server-side.
      await api.patch(`/transactions/${tx.id}`, {
        is_extra_amortization: false,
        extra_amort_mode: "",
      });
      toast.success("Amortización extra desmarcada");
      setRefreshTick((t) => t + 1);
    } catch (e) {
      logger.warn("unlink extra", e);
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  if (!debt) return null;

  const currency = debt.currency || "USD";
  const totalInterest = rows.reduce((s, r) => s + r.interest, 0);
  const totalPaid = rows.reduce((s, r) => s + r.payment, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl bg-white max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="font-display text-2xl">Amortización — {debt.creditor}</DialogTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="open-simulator-btn"
              onClick={() => setSimOpen(true)}
              className="gap-2"
            >
              <Calculator className="w-4 h-4" /> Simular reamortización
            </Button>
          </div>
        </DialogHeader>

        {rows.length === 0 ? (
          <div className="p-8 text-[#5F7368] text-sm">
            Para ver la amortización, edita esta deuda y agrega <b>Plazo (meses)</b>, <b>Fecha de inicio</b> y (opcional)
            <b> Cuota mensual</b> o <b>Tasa de interés</b>.
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col gap-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Cuota mensual" value={formatCurrency(monthly, currency)} />
              <Stat label="Total a pagar" value={formatCurrency(totalPaid, currency)} />
              <Stat label="Total intereses" value={formatCurrency(totalInterest, currency)} tone="text-[#D95D39]" />
              <Stat label="Tipo interés" value={debt.rate_type === "variable" ? "Variable" : "Fija"} sub={`${Number(debt.interest_rate || 0).toFixed(2)}% anual`} />
            </div>

            {ytd && (
              <div
                data-testid="amort-ytd-widget"
                className="rounded-xl border border-[#EBEBEA] bg-gradient-to-br from-[#FAFAF9] to-white px-5 py-4"
              >
                <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#1C7C54]">
                      Resumen {ytd.year}
                    </div>
                    <div className="text-sm text-[#5F7368]">
                      {ytd.regular_payment_count_ytd} cuota{ytd.regular_payment_count_ytd === 1 ? "" : "s"}
                      {ytd.extra_amortization_count_ytd > 0 && (
                        <> · <span className="text-[#1C7C54] font-medium">{ytd.extra_amortization_count_ytd} amortización{ytd.extra_amortization_count_ytd === 1 ? "" : "es"} extra</span></>
                      )}
                    </div>
                  </div>
                  {ytd.expected_monthly_payment > 0 && (
                    <div className="text-xs text-[#8A9992] tabular-nums text-right">
                      Cuota esperada · <b className="text-[#0A110E]">{formatCurrency(ytd.expected_monthly_payment, currency)}</b>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MiniStat
                    testid="ytd-principal"
                    label="Capital pagado"
                    value={formatCurrency(ytd.principal_ytd, currency)}
                    tone="text-[#1C7C54]"
                  />
                  <MiniStat
                    testid="ytd-interest"
                    label="Intereses pagados"
                    value={formatCurrency(ytd.interest_ytd, currency)}
                    tone="text-[#D95D39]"
                  />
                  <MiniStat
                    testid="ytd-extra"
                    label="Amort. extra"
                    value={formatCurrency(ytd.extra_amortization_ytd, currency)}
                    tone={ytd.extra_amortization_ytd > 0 ? "text-[#1C7C54]" : "text-[#8A9992]"}
                  />
                  <MiniStat
                    testid="ytd-projection"
                    label={`Proyección ${ytd.year}`}
                    value={formatCurrency(ytd.projected_total_eoy, currency)}
                    sub={`Capital ${formatCurrency(ytd.projected_principal_eoy, currency)}`}
                  />
                </div>
              </div>
            )}

            <div className="h-56 w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={rows.map((r) => ({ n: r.n, Capital: r.principal, Interes: r.interest }))}>
                  <defs>
                    <linearGradient id="cap" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1C7C54" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#1C7C54" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="int" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D95D39" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#D95D39" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEA" vertical={false} />
                  <XAxis dataKey="n" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={60}
                    tickFormatter={(v) => formatCurrency(v, currency).replace(/\.00$/, "")}
                  />
                  <Tooltip
                    formatter={(v) => formatCurrency(v, currency)}
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                  />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                  <Area type="monotone" dataKey="Capital" stackId="1" stroke="#1C7C54" fill="url(#cap)" />
                  <Area type="monotone" dataKey="Interes" stackId="1" stroke="#D95D39" fill="url(#int)" />
                  {extras.map((ex) => (
                    <ReferenceLine
                      key={`extra-${ex.id}`}
                      x={ex.n}
                      stroke="#1C7C54"
                      strokeDasharray="4 3"
                      strokeWidth={1.5}
                      label={{
                        value: `⚡ ${formatCurrency(Math.abs(ex.amount || 0), currency).replace(/\.00$/, "")}`,
                        position: "top",
                        fill: "#1C7C54",
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {extras.length > 0 && (
              <div
                data-testid="extras-timeline"
                className="rounded-xl border border-[#1C7C54]/30 bg-[#1C7C54]/5 px-4 py-3"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-3.5 h-3.5 text-[#1C7C54]" />
                  <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#1C7C54]">
                    Timeline de amortizaciones extra · {extras.length}
                  </span>
                </div>
                <ul className="divide-y divide-[#1C7C54]/10">
                  {extras.map((ex) => (
                    <li
                      key={ex.id}
                      data-testid={`extra-item-${ex.id}`}
                      className="flex items-center justify-between gap-3 py-2 text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[#0A110E] font-medium tabular-nums">
                          {formatCurrency(Math.abs(ex.amount || 0), currency)}
                          <span className="ml-2 text-[10px] uppercase tracking-[0.1em] font-bold text-[#1C7C54]/70">
                            {ex.extra_amort_mode === "reduce_payment" ? "Reduce cuota" : "Reduce plazo"}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#5F7368] truncate">
                          {(() => {
                            try { return format(parseISO(ex.date), "dd MMM yyyy"); }
                            catch (e) { return ex.date; }
                          })()}
                          {ex.description ? ` · ${ex.description}` : ""}
                        </div>
                      </div>
                      <div className="text-xs text-[#8A9992] tabular-nums whitespace-nowrap">
                        Saldo: <b className="text-[#0A110E]">{formatCurrency(ex.balance_after, currency)}</b>
                      </div>
                      <button
                        type="button"
                        title="Quitar marca de extra"
                        data-testid={`extra-unlink-${ex.id}`}
                        onClick={() => unlinkExtra(ex)}
                        className="p-1.5 rounded-md hover:bg-white/70 text-[#5F7368] hover:text-[#D95D39]"
                      >
                        <Link2Off className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex-1 overflow-auto border border-[#EBEBEA] rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAF9] sticky top-0 z-10">
                  <tr className="text-xs uppercase tracking-[0.08em] text-[#8A9992]">
                    <th className="text-left px-4 py-3">#</th>
                    <th className="text-left px-4 py-3">Fecha</th>
                    <th className="text-right px-4 py-3">Cuota</th>
                    <th className="text-right px-4 py-3">Capital</th>
                    <th className="text-right px-4 py-3">Interés</th>
                    <th className="text-right px-4 py-3">Saldo</th>
                  </tr>
                </thead>
                <tbody data-testid="amort-table">
                  {rows.map((r) => (
                    <tr
                      key={r.n}
                      className={`border-t border-[#EBEBEA] ${
                        r.isCurrent ? "bg-[#1C7C54]/5" : r.isPast ? "opacity-60" : ""
                      }`}
                    >
                      <td className="px-4 py-2 tabular-nums text-[#5F7368]">{r.n}</td>
                      <td className="px-4 py-2 text-[#5F7368]">{r.date}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">{formatCurrency(r.payment, currency)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-[#1C7C54]">{formatCurrency(r.principal, currency)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-[#D95D39]">{formatCurrency(r.interest, currency)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(r.balance, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {currentIdx > 0 && (
              <p className="text-xs text-[#8A9992]">
                Estás aproximadamente en la cuota <b>{currentIdx}</b> de {rows.length} según tu saldo pendiente.
              </p>
            )}
          </div>
        )}
      </DialogContent>

      <MortgageSimulatorDialog
        open={simOpen}
        onOpenChange={setSimOpen}
        debt={debt}
      />
    </Dialog>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-[#EBEBEA] bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#8A9992]">{label}</div>
      <div className={`font-display text-lg font-semibold tabular-nums mt-1 ${tone || "text-[#0A110E]"}`}>{value}</div>
      {sub && <div className="text-xs text-[#8A9992] tabular-nums">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, sub, tone, testid }) {
  return (
    <div className="rounded-lg bg-white border border-[#EBEBEA]/60 px-3 py-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.12em] font-bold text-[#8A9992]">{label}</div>
      <div className={`font-display text-base font-semibold tabular-nums mt-0.5 ${tone || "text-[#0A110E]"}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#8A9992] tabular-nums truncate">{sub}</div>}
    </div>
  );
}
